<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import useWordOfTheDayStore from '../store';
import SwedishKeyboard from './SwedishKeyboard.vue';
import Flower from './Flower.vue';
import { useDisplay } from 'vuetify';

const store = useWordOfTheDayStore();
const { word, selectedLetters, gameState, guesses, guessedCorrectWord } = storeToRefs(store);

const { lgAndUp, width } = useDisplay();

const letterSize = computed(() => {
  const wordLength = word.value.length;
  const availableWidth = width.value * 0.9; // 90% of viewport width

  // Account for gaps: each character takes 1 unit, each gap takes 0.5 units
  // Total units = wordLength + (wordLength - 1) * 0.5 = wordLength * 1.5 - 0.5
  const totalUnits = wordLength * 1.5 - 0.5;
  const idealLetterWidth = availableWidth / totalUnits;

  // Cap at reasonable min/max values
  const minSize = 1.2; // rem
  const maxSize = 4; // rem
  const calculatedSize = Math.min(maxSize, Math.max(minSize, idealLetterWidth / 16));

  return `${calculatedSize}rem`;
});

const gapSize = computed(() => `calc(${letterSize.value} * 0.5)`);
</script>

<template>
  <div class="the-game">
    <h1 class="title">Dagens ord</h1>
    <div class="tries-left">{{ 10 - guesses }} försök kvar</div>

    <Flower class="game-flower" />
    <div class="progress pb-4">
      <span v-for="(char, index) in word" :key="index" class="progress-letter">
        <span :class="[{ visible: selectedLetters.has(char) }]" class="letter">
          {{ char }}
        </span>
      </span>
    </div>

    <SwedishKeyboard
      v-if="gameState !== 'game-over'"
      @letter-click="(letter: string) => store.makeGuess(letter)"
      :guessed-letters="selectedLetters"
      :current-word="word"
      :disabled="gameState !== 'game'"
    />
    <span v-if="gameState === 'game-over'" class="game-over">{{ guessedCorrectWord ? 'Bra jobbat!' : 'Tyvärr!' }}</span>
    <div class="d-flex actions">
      <v-btn
        :height="lgAndUp ? '56px' : '48px'"
        v-if="gameState === 'game-over' && !guessedCorrectWord"
        @click="store.clear"
        variant="outlined"
        prepend-icon="mdi-repeat"
        text="Prova igen"
        class="mr-4 action-btn"
      ></v-btn>
      <v-btn
        :height="lgAndUp ? '56px' : '48px'"
        v-if="gameState === 'game-over'"
        @click="store.setGameState('results')"
        variant="outlined"
        append-icon="mdi-chevron-right"
        class="action-btn"
        :text="guessedCorrectWord ? 'Utforska ordet' : 'Se rätt svar'"
      >
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
.game-over {
  font-family: 'Chalkduster', sans-serif;
  font-size: clamp(1.5rem, 3vw, 4rem);
}

.progress {
  display: flex;
  justify-content: center;
  max-width: 90vw;
  flex: 0 0 auto;
  gap: v-bind(gapSize);
}

.progress-letter {
  font-size: v-bind(letterSize);
  width: v-bind(letterSize);
  min-width: 1.2rem;
  display: flex;
  align-items: end;
  justify-content: center;
  border-bottom: 2px solid currentColor;
  font-family: 'Chalkduster', sans-serif;

  .letter {
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
    &.visible {
      opacity: 1;
    }
  }
}

.the-game {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(0.5rem, 2vh, 2rem);
  padding: clamp(0.75rem, 3vh, 2.5rem) 1rem;
}

:deep(.game-flower) {
  flex: 1 1 0;
  min-height: 0;
  max-height: 45vh;
}

.actions {
  flex: 0 0 auto;
}

.title {
  position: absolute;
  top: 140px;
  left: 20px;
  transform: rotate(-10deg);
  transform-origin: top left;
  font-size: 2.25rem;
  font-weight: bold;
  font-family: 'Chalkduster', sans-serif;
}

.tries-left {
  position: absolute;
  top: calc(64px + 2rem);
  right: 2rem;
  font-size: 1rem;
}
</style>
